import { z } from 'zod';

/**
 * The stable slug a venue's city is **grouped and filtered** by.
 *
 * A venue carries two spellings of its city and they are not redundant: this
 * key on the base row is what the directory groups by, what the admin filter
 * compares, and what P25e's member browse facets on; the translated `city` in
 * `venue_translations` is what a human reads. A translated label cannot be an
 * equality filter shared between an Arabic member and an English operator, and
 * a slug is not something to show a member.
 *
 * Same grammar as `StableKeySchema`, deliberately **not** the same rule. That
 * key is immutable forever because profile answers are addressed by it; a city
 * key is an editable grouping value on a venue row. Sharing the schema would
 * quietly attach that immutability promise to something that does not keep it.
 *
 * Its real job is refusing the spellings that split one city into two entries
 * in a dropdown — `Riyadh`, `riyadh-city`, `riyadh ` — which is why the same
 * rule is enforced three times over: here, by the admin combobox's
 * `validateCreate`, and by the `venues_city_key_format` CHECK, so a row cannot
 * arrive past it from a seeder or an import either.
 */
export const CITY_KEY_MAX_LENGTH = 64;

export const CITY_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

export const CityKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(CITY_KEY_MAX_LENGTH)
  .regex(
    CITY_KEY_PATTERN,
    'A city key is lower-case ASCII, starts with a letter, and uses underscores: e.g. "al_khobar".',
  );

export type CityKey = z.infer<typeof CityKeySchema>;

/** Narrows a raw query parameter before it is used as a filter value. */
export function isCityKey(value: string): boolean {
  return CityKeySchema.safeParse(value).success;
}
